import { Badge, EmptyState, Tabs, When } from '@/components';
import { cn } from '@/lib/cn';
import type { Changelog } from '@uchat-team-management/shared';
import { For, Show, createSignal } from 'solid-js';

const rowClass =
  'flex gap-2 rounded-lg px-2 py-1.5 transition-colors duration-200 ease-out hover:bg-neutral-100 motion-reduce:transition-none dark:hover:bg-neutral-800';

const ExternalLink = (props: { url: string; children: string; class?: string }) => (
  <Show when={props.url} fallback={<span class={props.class}>{props.children}</span>}>
    <a
      href={props.url}
      target="_blank"
      rel="noreferrer"
      class={cn('text-brand-600 hover:underline dark:text-brand-400', props.class)}
    >
      {props.children}
    </a>
  </Show>
);

/** GitHub author chip: avatar + mapped app-user name, falling back to the login. */
const Author = (props: {
  login: string | null | undefined;
  authorUsers: Changelog['authorUsers'];
}) => (
  <Show when={props.login}>
    {(login) => (
      <span class="flex shrink-0 items-center gap-1.5 text-xs text-neutral-500">
        <img
          src={`https://github.com/${login()}.png?size=40`}
          alt=""
          loading="lazy"
          class="h-4 w-4 rounded-full"
        />
        {props.authorUsers?.[login().toLowerCase()]?.displayName ?? login()}
      </span>
    )}
  </Show>
);

/** Presentational PR/commit/task tabs over a computed change log. */
export const ChangelogView = (props: { changelog: Changelog }) => {
  const [active, setActive] = createSignal<string>('prs');
  // Newest-first for display. Snapshots store GitHub compare order (oldest→newest)
  // and old frozen snapshots carry no dates, so ordering is positional/by-number,
  // never date-sorted.
  const prs = () => [...props.changelog.prs].sort((a, b) => b.number - a.number);
  const commits = () => [...props.changelog.commits].reverse();
  const tasksFor = (source: 'clickup' | 'linear') =>
    props.changelog.tasks
      .filter((t) => t.source === source)
      .sort((a, b) => b.ref.localeCompare(a.ref, undefined, { numeric: true }));
  const tabs = () => [
    { id: 'prs', label: 'PRs', count: props.changelog.prs.length },
    { id: 'commits', label: 'Commits', count: props.changelog.commits.length },
    { id: 'clickup', label: 'ClickUp Tasks', count: tasksFor('clickup').length },
    { id: 'linear', label: 'Linear Tasks', count: tasksFor('linear').length },
  ];

  return (
    <>
      <Tabs tabs={tabs()} active={active()} onChange={setActive} class="mb-3" />
      <Show when={active() === 'prs'}>
        <Show
          when={props.changelog.prs.length > 0}
          fallback={<EmptyState title="No pull requests" />}
        >
          <ul class="flex flex-col gap-1 text-sm">
            <For each={prs()}>
              {(pr) => (
                <li class={cn(rowClass, 'items-center')}>
                  <ExternalLink url={pr.url}>{`#${pr.number} ${pr.title}`}</ExternalLink>
                  <span class="ml-auto flex shrink-0 items-center gap-2">
                    <Author login={pr.authorLogin} authorUsers={props.changelog.authorUsers} />
                    <When date={pr.mergedAt ?? pr.createdAt ?? undefined} />
                  </span>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </Show>
      <Show when={active() === 'commits'}>
        <Show
          when={props.changelog.commits.length > 0}
          fallback={<EmptyState title="No commits" />}
        >
          <ul class="flex flex-col gap-1 text-sm">
            <For each={commits()}>
              {(commit) => (
                <li class={cn(rowClass, 'items-start')}>
                  <code class="w-16 shrink-0 pt-0.5 text-xs text-neutral-500">
                    {commit.sha.slice(0, 7)}
                  </code>
                  <ExternalLink url={commit.url}>{commit.subject}</ExternalLink>
                  <span class="ml-auto flex shrink-0 items-center gap-2 pt-0.5">
                    <Author login={commit.authorLogin} authorUsers={props.changelog.authorUsers} />
                    <When date={commit.date ?? undefined} />
                  </span>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </Show>
      <For each={['clickup', 'linear'] as const}>
        {(source) => (
          <Show when={active() === source}>
            <Show
              when={tasksFor(source).length > 0}
              fallback={<EmptyState title={`No ${source} tasks referenced`} />}
            >
              <ul class="flex flex-col gap-1 text-sm">
                <For each={tasksFor(source)}>
                  {(task) => {
                    // Latest cached ClickUp (UCH-273) / Linear (UCH-270) metadata;
                    // unresolved refs have no entry, so fallbacks keep one renderer.
                    const detail = () => props.changelog.taskDetails?.[task.ref];
                    return (
                      <li class={cn(rowClass, 'items-start')}>
                        <ExternalLink
                          url={detail()?.url || task.url}
                          class="min-w-28 shrink-0 whitespace-nowrap"
                        >
                          {task.ref}
                        </ExternalLink>
                        <div class="flex min-w-0 flex-col items-start gap-0.5">
                          <span class="text-neutral-500">{detail()?.title || task.title}</span>
                          <span class="flex items-center gap-1.5">
                            <Show when={task.status || detail()?.status}>
                              <span class="text-[11px] font-medium uppercase tracking-wide text-neutral-400">
                                {task.status || detail()?.status}
                              </span>
                            </Show>
                            <Show
                              when={
                                props.changelog.frozenAt &&
                                task.status &&
                                detail() &&
                                detail()?.status !== task.status
                              }
                            >
                              <Badge
                                tone="info"
                                class="transition-opacity duration-200 ease-out motion-reduce:transition-none"
                              >
                                now: {detail()?.status}
                              </Badge>
                            </Show>
                          </span>
                        </div>
                        <span class="ml-auto flex shrink-0 items-center gap-2 pt-0.5">
                          <When date={detail()?.refreshedAt} />
                        </span>
                      </li>
                    );
                  }}
                </For>
              </ul>
            </Show>
          </Show>
        )}
      </For>
    </>
  );
};
